import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './CalendarWeek.module.scss';
import { CalendarWorkoutThumbnail } from './CalendarWorkoutThumbnail';
import { CalendarEmptySlot } from './CalendarEmptySlot';
import Menu from '../atoms/Menu';
import { Day } from '../../constants';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import type { CalendarDaySlot } from '../../types';

export interface DraggedWorkoutLocation {
  weekIndex: number;
  day: Day;
}

export interface CalendarWeekProps {
  weekIndex: number;
  days: CalendarDaySlot[];

  onAddWorkout: (weekIndex: number, day: Day) => void;
  onEditWorkout: (weekIndex: number, day: Day) => void;
  onRemoveProgramItem: (weekIndex: number, day: Day) => void;

  onDuplicateWeek?: (weekIndex: number) => void;
  onDeleteWeek?: (weekIndex: number) => void;
  onMoveWorkout?: (
    fromWeekIndex: number,
    fromDay: Day,
    toWeekIndex: number,
    toDay: Day,
  ) => void;
  draggingWorkout?: DraggedWorkoutLocation | null;
  setDraggingWorkout?: (value: DraggedWorkoutLocation | null) => void;
}

export const CalendarWeek: React.FC<CalendarWeekProps> = ({
  weekIndex,
  days,
  onAddWorkout,
  onEditWorkout,
  onRemoveProgramItem,
  onDuplicateWeek,
  onDeleteWeek,
  onMoveWorkout,
  draggingWorkout,
  setDraggingWorkout,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [weekOptionsOpen, setWeekOptionsOpen] = useState(false);
  const [dragOverDay, setDragOverDay] = useState<Day | null>(null);

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
    setWeekOptionsOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setWeekOptionsOpen(false);
  };

  const handleDragStart = (event: React.DragEvent, day: Day): void => {
    if (!setDraggingWorkout) return;
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    setMenuAnchorEl(null);
    setWeekOptionsOpen(false);
    setDraggingWorkout({ weekIndex, day });
  };

  const handleDragEnd = (): void => {
    setDragOverDay(null);
    setDraggingWorkout?.(null);
  };

  const handleDragEnter = (
    event: React.DragEvent,
    day: Day,
    hasItem: boolean,
  ) => {
    if (!draggingWorkout || hasItem) return;
    event.preventDefault();
    setDragOverDay(day);
  };

  const handleDragOver = (
    event: React.DragEvent,
    day: Day,
    hasItem: boolean,
  ) => {
    if (!draggingWorkout || hasItem) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDrop = (event: React.DragEvent, day: Day, hasItem: boolean) => {
    if (!draggingWorkout) return;
    event.preventDefault();
    setDragOverDay(null);
    if (hasItem || !onMoveWorkout) {
      setDraggingWorkout?.(null);
      return;
    }
    onMoveWorkout(
      draggingWorkout.weekIndex,
      draggingWorkout.day,
      weekIndex,
      day,
    );
    setDraggingWorkout?.(null);
  };

  const getMenuOptions = () => {
    const options: Array<{
      onClick: () => void;
      label: string;
      icon: React.ReactNode;
    }> = [];

    if (onDuplicateWeek) {
      options.push({
        onClick: () => {
          handleCloseMenu();
          onDuplicateWeek(weekIndex);
        },
        label: 'Duplicate Week',
        icon: <ContentCopy color="primary" />,
      });
    }

    if (onDeleteWeek) {
      options.push({
        onClick: () => {
          handleCloseMenu();
          onDeleteWeek(weekIndex);
        },
        label: 'Delete Week',
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      });
    }

    return options;
  };

  const renderWeekMenu = () => (
    <div className={styles.fadeInMenu}>
      <PendingOutlinedIcon
        // @ts-ignore
        onClick={(e) => handleClickMenu(e)}
        className={styles.optionsBtn}
        color="primary"
      />
      <Menu
        anchorEl={menuAnchorEl}
        isOpen={weekOptionsOpen}
        onClose={handleCloseMenu}
        options={getMenuOptions()}
      />
    </div>
  );

  const renderCalendarDay = (slot: CalendarDaySlot, index: number) => {
    const { day, item, date, isStartDay } = slot;
    const dateText = date ? date.getDate() : '';
    const dayText = day.toUpperCase();
    const isToday = date
      ? date.toDateString() === new Date().toDateString()
      : false;
    const isDragging = Boolean(draggingWorkout);
    const isSource =
      draggingWorkout?.weekIndex === weekIndex && draggingWorkout?.day === day;
    const isDropTarget = isDragging && !item;
    const isDraggingOver = dragOverDay === day && isDropTarget;

    return (
      <div
        className={classNames(styles.dayContainer, {
          [styles.droppable]: isDropTarget,
          [styles.draggingOver]: isDraggingOver,
          [styles.notDroppable]: isDragging && Boolean(item),
          [styles.dragSource]: isSource,
        })}
        key={`${weekIndex}-${day}-${index}`}
        onDragEnter={(event) => handleDragEnter(event, day, Boolean(item))}
        onDragOver={(event) => handleDragOver(event, day, Boolean(item))}
        onDragLeave={handleDragLeave}
        onDrop={(event) => handleDrop(event, day, Boolean(item))}
      >
        <div className={styles.dayHeader}>
          {date && <div className={styles.text}>{dateText}</div>}
          <div className={styles.text}>{dayText}</div>
        </div>

        {item ? (
          <CalendarWorkoutThumbnail
            routine={item.routine}
            isStartDay={Boolean(isStartDay)}
            isSelected={isToday}
            onEditWorkout={() => onEditWorkout(weekIndex, day)}
            onRemoveProgramItem={() => onRemoveProgramItem(weekIndex, day)}
            isDraggable={Boolean(setDraggingWorkout)}
            isDragging={isSource}
            onDragStart={(event) => handleDragStart(event, day)}
            onDragEnd={handleDragEnd}
          />
        ) : (
          <CalendarEmptySlot
            isStartDay={Boolean(isStartDay)}
            isSelected={isToday}
            onAddWorkout={() => onAddWorkout(weekIndex, day)}
            isDroppable={isDropTarget}
            isDraggingOver={isDraggingOver}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={styles.wrapper}
      onMouseEnter={(e) => setMenuAnchorEl(e.currentTarget)}
      onMouseLeave={() => setMenuAnchorEl(null)}
    >
      <div className={styles.container}>
        {days.map((slot, index) => renderCalendarDay(slot, index))}
      </div>
      <div className={styles.menuContainer}>
        {menuAnchorEl && renderWeekMenu()}
      </div>
    </div>
  );
};
